import { ApplicationController } from '@aha-app/mvc';
import WidgetsController from '../../../controllers/WidgetsController';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import type SidePanelController from '../../../components/SidePanel/controllers/SidePanelController';

interface State {
  widget?: VotingSessionWidget;
  remainingTime: number;
  running: boolean;
}

interface ControllerProps {
  widget: VotingSessionWidget;
}

class VotingSessionWidgetController extends ApplicationController<
  State,
  ControllerProps
> {
  widgetsController: WidgetsController;

  static initialState: State = {
    remainingTime: 0,
    running: false,
  };

  async initialize({ widget }): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.widgetsController = this.findControllerInstance(WidgetsController)!;

    this.setWidget(widget);
  }

  setWidget(widget: VotingSessionWidget | undefined): void {
    this.state.widget?.removeEventListener('tick', this.updateRemainingTime);
    if (widget) {
      this.state.widget = widget;
      this.state.remainingTime = widget.remainingDuration;
      widget.addEventListener('tick', this.updateRemainingTime);
    }
  }

  updateRemainingTime = () => {
    this.state.remainingTime = this.state.widget?.remainingDuration ?? 0;
  };

  destroy(): void {
    this.state.widget?.removeEventListener('tick', this.updateRemainingTime);
  }

  get votesRemaining(): number {
    if (!this.userId) return 0;

    return this.state.widget?.votesRemaining(this.userId) ?? 0;
  }

  get isFinishedVoting(): boolean {
    if (!this.userId) return true;

    return this.state.widget?.userFinishedVoting(this.userId) ?? true;
  }

  get userId() {
    const { whiteboardController } = this.widgetsController;
    const { userId } = whiteboardController.state;

    return userId;
  }

  sidePanelIsOpen() {
    const sidePanelController = this.widgetsController?.whiteboardController
      ?.sidePanelController as SidePanelController | undefined;
    if (!sidePanelController) return false;
    if (!sidePanelController.state.sidePanelOpen) return false;

    return sidePanelController.activeMenuItem()?.id === 'voting';
  }

  actionFinishVoting() {
    if (!this.state.widget) return;
    if (!this.userId) return;
    if (this.state.widget?.userFinishedVoting(this.userId)) return;

    this.state.widget.markUserAsFinished(this.userId);
    this.widgetsController.whiteboardController.canvas.requestRenderAll();
  }

  actionSetVotesForObject(objectId: string, count: number): void {
    if (!this.state.widget) return;
    if (!this.userId) return;

    this.state.widget.setVotesForObject(objectId, this.userId, count);
  }
}

export default VotingSessionWidgetController;
